import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { motion } from "framer-motion";
import {
  Users, CreditCard, AlertTriangle, ShieldAlert, Loader2,
  ArrowLeft, Crown, UserX, Fingerprint, Activity,
} from "lucide-react";
import { useAppStore } from "@/store";
import { apiFetch } from "@/lib/api-fetch";

interface AdminStats {
  totalUsers: number;
  activeSubscriptions: number;
  canceledSubscriptions: number;
  freeUsers: number;
  verifiedEmails: number;
  freeAtLimit: number;
}

interface AdminUser {
  id: string;
  email: string;
  displayName: string | null;
  emailVerified: boolean;
  subscriptionStatus: string;
  subscriptionPlan: string | null;
  createdAt: string;
  postCount: number;
  lastActiveAt: string | null;
}

interface AbuseEntry {
  fingerprint: string;
  sessionCount: number;
  lastSeen: string;
}

interface Pagination {
  page: number;
  limit: number;
  totalPages: number;
  totalUsers: number;
}

interface EventsData {
  last7Days: Record<string, number>;
  last30Days: Record<string, number>;
}

interface AdminData {
  stats: AdminStats;
  users: AdminUser[];
  pagination: Pagination;
  abuseLog: AbuseEntry[];
}

function KpiCard({ icon: Icon, label, value, color }: {
  icon: typeof Users;
  label: string;
  value: number | string;
  color: string;
}) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
      </div>
      <p className="text-3xl font-display font-bold">{value}</p>
    </div>
  );
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    canceled: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    none: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
  };
  const s = styles[status] || styles.none;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${s}`}>
      {status === "none" ? "free" : status}
    </span>
  );
}

export default function Admin() {
  const { authToken, userEmail } = useAppStore();
  const [, navigate] = useLocation();
  const [data, setData] = useState<AdminData | null>(null);
  const [eventsData, setEventsData] = useState<EventsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  useEffect(() => {
    if (!authToken) {
      navigate("/");
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");
        const [overviewRes, eventsRes] = await Promise.all([
          apiFetch(`${baseUrl}/api/admin/overview?page=${page}&limit=${PAGE_SIZE}`, {
            headers: { Authorization: `Bearer ${authToken}` },
          }),
          apiFetch(`${baseUrl}/api/admin/events`, {
            headers: { Authorization: `Bearer ${authToken}` },
          }),
        ]);

        if (overviewRes.status === 403) {
          navigate("/");
          return;
        }

        if (!overviewRes.ok) {
          const body = await overviewRes.json().catch(() => ({}));
          setError(body.error || "Failed to load admin data");
          return;
        }

        setData(await overviewRes.json());
        if (eventsRes.ok) {
          setEventsData(await eventsRes.json());
        }
      } catch {
        setError("Failed to load admin data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authToken, navigate, page]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="bg-destructive/10 border border-destructive/20 rounded-2xl p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-destructive mx-auto mb-3" />
          <p className="text-sm text-destructive font-medium">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { stats, users, abuseLog } = data;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-6xl mx-auto space-y-8"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldAlert className="w-5 h-5 text-primary" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Admin</span>
          </div>
          <h1 className="text-3xl font-display font-bold">Dashboard</h1>
          <p className="text-muted-foreground mt-1 text-sm">Logged in as {userEmail}</p>
        </div>
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to app
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Users} label="Total Users" value={stats.totalUsers} color="bg-primary/10 text-primary" />
        <KpiCard icon={Crown} label="Active Subs" value={stats.activeSubscriptions} color="bg-emerald-500/10 text-emerald-400" />
        <KpiCard icon={UserX} label="Free at Limit" value={stats.freeAtLimit} color="bg-amber-500/10 text-amber-400" />
        <KpiCard icon={Fingerprint} label="Flagged Fingerprints" value={abuseLog.length} color="bg-red-500/10 text-red-400" />
      </div>

      <section className="space-y-4">
        <h2 className="text-xl font-display font-bold flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          Users ({data.pagination.totalUsers})
        </h2>
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/30">
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Email</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Signed Up</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Plan</th>
                  <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Posts</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Last Active</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-border/50 hover:bg-secondary/20 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground truncate max-w-[200px]">{user.email}</span>
                        {!user.emailVerified && (
                          <span className="text-[10px] font-semibold text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">unverified</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{formatDate(user.createdAt)}</td>
                    <td className="px-4 py-3"><StatusBadge status={user.subscriptionStatus} /></td>
                    <td className="px-4 py-3 text-muted-foreground">{user.subscriptionPlan || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono font-medium">{user.postCount}</td>
                    <td className="px-4 py-3 text-muted-foreground">{formatDate(user.lastActiveAt)}</td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No users yet</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {data.pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-xs text-muted-foreground">
                Page {data.pagination.page} of {data.pagination.totalPages}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-border bg-secondary/30 hover:bg-secondary/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(data!.pagination.totalPages, p + 1))}
                  disabled={page >= data.pagination.totalPages}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-border bg-secondary/30 hover:bg-secondary/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {eventsData && (
        <section className="space-y-4">
          <h2 className="text-xl font-display font-bold flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            Activity
          </h2>
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/30">
                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Event</th>
                    <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Last 7 days</th>
                    <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Last 30 days</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const allEvents = new Set([
                      ...Object.keys(eventsData.last7Days),
                      ...Object.keys(eventsData.last30Days),
                    ]);
                    const sorted = [...allEvents].sort();
                    if (sorted.length === 0) {
                      return (
                        <tr>
                          <td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">No events recorded yet</td>
                        </tr>
                      );
                    }
                    return sorted.map((eventName) => (
                      <tr key={eventName} className="border-b border-border/50 hover:bg-secondary/20 transition-colors">
                        <td className="px-4 py-3 font-medium text-foreground">{eventName.replace(/_/g, " ")}</td>
                        <td className="px-4 py-3 text-right font-mono font-medium">{eventsData.last7Days[eventName] ?? 0}</td>
                        <td className="px-4 py-3 text-right font-mono font-medium">{eventsData.last30Days[eventName] ?? 0}</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      <section className="space-y-4 pb-8">
        <h2 className="text-xl font-display font-bold flex items-center gap-2">
          <Fingerprint className="w-5 h-5 text-red-400" />
          Abuse Log
        </h2>
        <p className="text-sm text-muted-foreground">
          Fingerprints linked to more than one session, sorted by most recent activity.
        </p>
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/30">
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Fingerprint Hash</th>
                  <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Sessions</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Last Seen</th>
                </tr>
              </thead>
              <tbody>
                {abuseLog.map((entry) => (
                  <tr key={entry.fingerprint} className="border-b border-border/50 hover:bg-secondary/20 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-foreground truncate max-w-[300px]">{entry.fingerprint}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-mono font-bold ${entry.sessionCount > 3 ? "text-red-400" : "text-amber-400"}`}>
                        {entry.sessionCount}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{formatDate(entry.lastSeen)}</td>
                  </tr>
                ))}
                {abuseLog.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">No abuse entries detected</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </motion.div>
  );
}
